// src/context/AuthContext.jsx

import React, {
    createContext,
    useContext,
    useEffect,
    useState,
} from "react";
import axios from "axios";

const API_BASE = "https://figazuresql.swancreate.com/api/swa";

/* ------------------------------------------------------------------ */
/*  Context scaffold                                                  */
/* ------------------------------------------------------------------ */
export const AuthContext = createContext({
    user: null,
    isAdmin: false,
    isSchoolUser: false,
    allocations: [],
    login: () => { },
    logout: () => { },
});

/* ------------------------------------------------------------------ */
/*  Provider                                                          */
/* ------------------------------------------------------------------ */
export const AuthProvider = ({ children }) => {
    /* ------------------------------------------------------------------ */
    /*  persist / hydrate user                                            */
    /* ------------------------------------------------------------------ */
    const [user, setUser] = useState(() => {
        const saved = localStorage.getItem("user");
        return saved ? JSON.parse(saved) : null;
    });

    const [allocations, setAllocations] = useState([]);

    /* ------------------------------------------------------------------ */
    /*  Whenever `user` changes load SchoolAllocation or SchoolMst       */
    /* ------------------------------------------------------------------ */
    useEffect(() => {
        if (!user) {
            localStorage.removeItem("user");
            setAllocations([]);
            return;
        }

        // persist user in localStorage
        localStorage.setItem("user", JSON.stringify(user));

        // ADMIN (RoleId === 1): fetch all sites from SchoolMst
        if (Number(user.RoleId) === 1) {
            (async () => {
                try {
                    const { data } = await axios.get(`${API_BASE}/SchoolMst`);
                    // map to our shape
                    const allSites = data.map((r) => ({
                        SchoolId: r.SchoolID,
                        SchoolName: r.SchoolName,
                    }));
                    // sort alphabetically by SchoolName
                    allSites.sort((a, b) =>
                        a.SchoolName.localeCompare(b.SchoolName)
                    );
                    console.log(
                        `[AuthContext] Admin: loaded ${allSites.length} sites from SchoolMst`
                    );
                    setAllocations(allSites);
                } catch (err) {
                    console.error(
                        "[AuthContext] failed to fetch SchoolMst for admin:",
                        err
                    );
                    setAllocations([]);
                }
            })();
            return;
        }

        // SCHOOL USER (RoleId === 2): fetch only their allocations
        if (Number(user.RoleId) === 2 && user.UserId) {
            (async () => {
                try {
                    const { data } = await axios.get(
                        `${API_BASE}/SchoolAllocation?userid=${user.UserId}`
                    );

                    const cleaned = data.filter(
                        (row) =>
                            row.userid?.toLowerCase() === user.UserId.toLowerCase() ||
                            (user.apiid &&
                                row.userid?.toLowerCase() === user.apiid.toLowerCase())
                    );

                    console.log(
                        `[AuthContext] SchoolAllocation: received ${data.length}, kept ${cleaned.length}`
                    );

                    // map & sort
                    const sites = cleaned
                        .map((r) => ({
                            SchoolId: r.schoolid || r.SchoolID,
                            SchoolName: r.schoolname || r.SchoolName,
                        }))
                        .sort((a, b) =>
                            a.SchoolName.localeCompare(b.SchoolName)
                        );

                    setAllocations(sites);
                } catch (err) {
                    console.error(
                        "[AuthContext] SchoolAllocation fetch failed:",
                        err
                    );
                    setAllocations([]);
                }
            })();
            return;
        }

        // other roles: no sites
        setAllocations([]);
    }, [user]);

    /* ------------------------------------------------------------------ */
    /*  login / logout helpers                                            */
    /* ------------------------------------------------------------------ */
    const login = (raw) => {
        setUser({
            UserId: raw.UserId,
            apiid: raw.apiid,
            RoleId: Number(raw.RoleId),
            FirstName: raw.FirstName,
            LastName: raw.LastName,
            Email: raw.Email,
        });
    };

    const logout = () => setUser(null);

    /* ------------------------------------------------------------------ */
    /*  Provide                                                           */
    /* ------------------------------------------------------------------ */
    return (
        <AuthContext.Provider
            value={{
                user,
                isAdmin: user?.RoleId === 1,
                isSchoolUser: user?.RoleId === 2,
                allocations,  // sorted list of sites
                login,
                logout,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};

/* Hook shortcut */
export const useAuth = () => useContext(AuthContext);
